<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import { useRouter } from 'vue-router';
import DialogResponseFooter from './ResponseFooter.vue';
import DialogResponseHeader from './ResponseHeader.vue';

const props = withDefaults(defineProps<{
	visible: boolean;
	title?: string;
	overline?: string;
	hasClose?: boolean;
	closeTo?: RouteLocationRaw;
}>(), {
	hasClose: true,
});

const emit = defineEmits<{
	'update:visible': [value: boolean];
}>();

const router = useRouter();

const close = () => {
	if (!props.visible || !props.hasClose) {
		return;
	}

	if (props.closeTo) {
		return router.push(props.closeTo);
	}

	emit('update:visible', false);
};
</script>

<template>
	<PvDialog
		:visible="props.visible"
		modal
		:show-header="false"
		:draggable="false"
		@update:visible="close"
	>
		<div class="dialog-response">
			<DialogResponseHeader
				:title="props.title"
				:overline="props.overline"
				:has-close="props.hasClose"
				@update:visible="close"
			/>

			<div class="dialog-response__body" v-if="$slots.default">
				<slot />
			</div>

			<DialogResponseFooter v-if="$slots.footer">
				<slot name="footer" />
			</DialogResponseFooter>
		</div>
	</PvDialog>
</template>

<style scoped lang="scss">
@layer hm-components
{
	.dialog-response
	{
		display:grid;
		grid-template-columns:minmax(0, 1fr);
		grid-template-rows:auto 1fr auto;
		gap:var(--spacing-2xl);
		width:min(100%, var(--container-xs));
		background-color:var(--bg-quaternary);
		padding:var(--spacing-2xl) var(--spacing-lg);
		overflow:hidden;
		min-height:100dvh;
		position:relative;

		@include responsive(xs)
		{
			grid-template-rows:none;
			min-height:auto;
			border-radius:var(--radius-3xl);
			box-shadow:var(--shadow-2xl);
			padding:var(--spacing-2xl);
		}
		@include responsive(sm)
		{
			padding:var(--spacing-4xl);
		}

		&__body
		{
			@include flex;
			flex-direction:column;
			position:relative;
		}
	}
}
</style>
